import { useState, useEffect, type FormEvent } from 'react';
import { AlertCircle, FileText, Info, Loader2, Play, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTenantId } from '@/lib/auth/hooks';
import { CubixApiError } from '@/lib/api/types';
import { cn } from '@/lib/utils';

import { AnalysisSkeleton } from '@/components/shared/AnalysisSkeleton';

import { useLatestNim, useTriggerNim } from './api';
import type { NimAnalysisDetail, NimCurrencyBreakdown, ScenarioCode } from './types';
import { useReportConfigs } from '@/features/parameters/reportConfigApi';
import type { NimParams } from '@/features/parameters/reportConfigApi';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Net Interest Margin — first new ALM engine after the gap engine.
 *
 * v0 uses placeholder rate inputs (configurable at the cx-alm side via
 * cubix.nim.baselineAssetRate / .baselineLiabilityRate). The v0 hint
 * banner explicitly flags this so analysts don't mistake the numbers for
 * a fully curve-aware calculation.
 *
 * Phase 4.1.NIM-FX: results now include a per-currency NII breakdown table
 * when the cashflow stream contains multiple currencies (e.g. IRS/CCS legs,
 * EUR-denominated bonds held by a MAD-reporting bank).
 *
 * Phase 4.2.REPCONF: report configs drive the primary selector; scenario
 * and horizon remain user-selectable as they are not stored in the config.
 */
export function NimAnalysisPage() {
  const tenantId = useTenantId();

  // ── Config selector ──────────────────────────────────────────────────────────
  const { data: allConfigs, isLoading: configsLoading } = useReportConfigs();
  const nimConfigs = allConfigs?.filter(c => c.reportType === 'NIM') ?? [];

  const firstId = nimConfigs[0]?.id ?? '';
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [date, setDate] = useState<string>(todayISO());
  const [horizonMonths, setHorizonMonths] = useState<number>(12);
  const [scenarioCode, setScenarioCode] = useState<ScenarioCode>('PARALLEL_UP_200');

  // Auto-select first config when list loads
  useEffect(() => {
    if (!selectedConfigId && firstId) {
      setSelectedConfigId(firstId);
      const cfg = nimConfigs[0];
      const p = cfg.parameters as NimParams;
      if (p.dateMode === 'SPECIFIC' && p.specificDate) setDate(p.specificDate);
      else setDate(todayISO());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstId]);

  function handleConfigChange(id: string) {
    setSelectedConfigId(id);
    const cfg = nimConfigs.find(c => c.id === id);
    if (!cfg) return;
    const p = cfg.parameters as NimParams;
    if (p.dateMode === 'SPECIFIC' && p.specificDate) setDate(p.specificDate);
    else setDate(todayISO());
  }

  // ── Calculation state ────────────────────────────────────────────────────────
  const latest = useLatestNim(date);
  const trigger = useTriggerNim();

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!selectedConfigId) return;
    trigger.mutate({
      calculationDate: date,
      horizonMonths,
      scenarioCode,
      force: true,
    });
  }

  const detail: NimAnalysisDetail | null = latest.data ?? null;

  const presets = nimConfigs.filter(c => c.isPreset);
  const custom  = nimConfigs.filter(c => !c.isPreset);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Net Interest Margin</h1>
        <p className="text-sm text-muted-foreground">
          Forward-looking NIM under a chosen rate-shift scenario — tenant{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{tenantId ?? '—'}</code>.
        </p>
      </header>

      <DegradedBanner detail={detail} />

      {/* ── Run form ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            Run NIM report
          </CardTitle>
          <CardDescription>
            Select a report configuration, choose a snapshot date and scenario, then run.
            The calculation date is pre-filled from the config when a specific date is stored.
            Manage report configs in <em>ALM Parameters → Report configurations</em>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="flex flex-wrap items-end gap-3" onSubmit={handleSubmit}>

            {/* Report selector */}
            <div className="space-y-1.5 flex-1 min-w-[220px]">
              <Label htmlFor="nim-config">Report</Label>
              {configsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground h-9">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading reports…
                </div>
              ) : nimConfigs.length === 0 ? (
                <p className="text-sm text-muted-foreground h-9 flex items-center">
                  No NIM report configs found. Create one in ALM Parameters.
                </p>
              ) : (
                <select
                  id="nim-config"
                  value={selectedConfigId}
                  onChange={e => handleConfigChange(e.target.value)}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {presets.length > 0 && (
                    <optgroup label="Standard reports">
                      {presets.map(c => (
                        <option key={c.id} value={c.id}>{c.name} ★</option>
                      ))}
                    </optgroup>
                  )}
                  {custom.length > 0 && (
                    <optgroup label="Custom reports">
                      {custom.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              )}
            </div>

            {/* Date */}
            <div className="space-y-1.5">
              <Label htmlFor="nim-date">Calculation date</Label>
              <Input
                id="nim-date"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                required
                className="w-44"
              />
            </div>

            {/* Horizon */}
            <div className="space-y-1.5">
              <Label htmlFor="nim-horizon">Horizon (months)</Label>
              <Input
                id="nim-horizon"
                type="number"
                min={1}
                max={120}
                value={horizonMonths}
                onChange={e => setHorizonMonths(Number(e.target.value) || 12)}
                required
                className="w-28"
              />
            </div>

            {/* Scenario */}
            <div className="space-y-1.5">
              <Label htmlFor="nim-scenario">Scenario</Label>
              <select
                id="nim-scenario"
                value={scenarioCode}
                onChange={e => setScenarioCode(e.target.value as ScenarioCode)}
                className={cn(
                  'h-9 rounded-md border border-input bg-background px-2 text-sm shadow-sm',
                  'focus:outline-none focus:ring-1 focus:ring-ring',
                )}
              >
                <option value="FLAT">Flat (no shock)</option>
                <option value="PARALLEL_UP_100">Parallel +100bp</option>
                <option value="PARALLEL_UP_200">Parallel +200bp</option>
                <option value="PARALLEL_UP_300">Parallel +300bp</option>
                <option value="PARALLEL_DOWN_100">Parallel -100bp</option>
                <option value="PARALLEL_DOWN_200">Parallel -200bp</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-end gap-2 self-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => latest.refetch()}
                disabled={latest.isFetching}
              >
                {latest.isFetching ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Reload
              </Button>
              <Button
                type="submit"
                disabled={!selectedConfigId || trigger.isPending || configsLoading}
              >
                {trigger.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                Run report
              </Button>
            </div>
          </form>

          {trigger.isError ? <ApiErrorBanner error={trigger.error} /> : null}
          {trigger.isSuccess ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Calculation queued · result_id{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                {trigger.data.resultId}
              </code>
              {' · '}engine {trigger.data.engineVersion}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {latest.isError ? <ApiErrorBanner error={latest.error} /> : null}

      {latest.isLoading && !detail
        ? <AnalysisSkeleton />
        : <ResultTiles detail={detail} loading={latest.isLoading} />
      }

      {/* Phase 4.1.NIM-FX — per-currency breakdown, only shown when present */}
      {detail?.currencyBreakdown && detail.currencyBreakdown.length > 0 ? (
        <CurrencyBreakdownCard rows={detail.currencyBreakdown} />
      ) : null}
    </div>
  );
}

// ── DegradedBanner ────────────────────────────────────────────────────────────

function DegradedBanner({ detail }: { detail: NimAnalysisDetail | null }) {
  if (!detail || !detail.degradedMode) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-800">
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <p>
        <strong>Degraded mode — placeholder rates used.</strong> No validated
        yield-curve snapshot was available for {detail.calculationDate}, so
        the engine fell back to the placeholder constants from{' '}
        <code>/settings/alm-parameters</code>. Upload + validate a curve on
        the <em>Parameters → Yield Curves</em> page to get curve-aware NIM.
      </p>
    </div>
  );
}

// ── ResultTiles ───────────────────────────────────────────────────────────────

function ResultTiles({
  detail,
  loading,
}: {
  detail: NimAnalysisDetail | null;
  loading: boolean;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Tile
        title="NIM Baseline"
        value={detail ? formatPct(detail.nimBaseline) : '—'}
        hint={loading ? 'Loading…' : detail ? 'No-shock scenario' : 'No result yet'}
      />
      <Tile
        title="NIM Stressed"
        value={detail ? formatPct(detail.nimStressed) : '—'}
        hint={detail ? `Scenario: ${detail.scenarioCode}` : '—'}
      />
      <Tile
        title="NIM Delta"
        value={detail ? formatPct(detail.nimDelta) : '—'}
        hint={
          detail
            ? Number(detail.nimDelta) >= 0
              ? 'Margin expands under shock'
              : 'Margin compresses under shock'
            : '—'
        }
        tone={detail && Number(detail.nimDelta) < 0 ? 'warn' : undefined}
      />
      <Tile
        title="Horizon"
        value={detail ? `${detail.horizonMonths} months` : '—'}
        hint={detail ? `Engine ${detail.engineVersion}` : '—'}
      />
    </div>
  );
}

function Tile({
  title,
  value,
  hint,
  tone,
}: {
  title: string;
  value: string;
  hint?: string;
  tone?: 'ok' | 'warn';
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className={tone === 'warn' ? 'text-2xl text-destructive' : 'text-2xl'}>
          {value}
        </CardTitle>
      </CardHeader>
      {hint ? (
        <CardContent className="pt-0 text-xs text-muted-foreground">{hint}</CardContent>
      ) : null}
    </Card>
  );
}

// ── CurrencyBreakdownCard (Phase 4.1.NIM-FX) ─────────────────────────────────

/**
 * Per-currency NII breakdown table.
 *
 * Shows each currency's earning-asset base and NIM (baseline vs stressed)
 * so ALM committees can see which currency contributes most to the margin
 * and where the interest-rate sensitivity is concentrated.
 *
 * IRS / CCS instruments produce two rows (pay leg + receive leg) because the
 * engine groups at cashflow level — one cashflow per currency per instrument.
 * Amounts are in reporting currency (converted via the cashflow FX rate).
 *
 * Columns: CCY | Assets | NII Baseline | NII Stressed | NIM Baseline | NIM Stressed | Δ
 */
function CurrencyBreakdownCard({ rows }: { rows: NimCurrencyBreakdown[] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Per-Currency NII Breakdown</CardTitle>
        <CardDescription>
          Net Interest Income contribution by currency. Amounts in reporting
          currency (converted via the cashflow FX rate at calculation date).
          IRS / CCS legs appear as separate currency rows.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="pb-2 pr-4 font-medium">CCY</th>
                <th className="pb-2 pr-4 text-right font-medium">Assets (rep. ccy)</th>
                <th className="pb-2 pr-4 text-right font-medium">NII Baseline</th>
                <th className="pb-2 pr-4 text-right font-medium">NII Stressed</th>
                <th className="pb-2 pr-4 text-right font-medium">NIM Baseline</th>
                <th className="pb-2 pr-4 text-right font-medium">NIM Stressed</th>
                <th className="pb-2 text-right font-medium">Δ NIM</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const delta = Number(row.nimStressed) - Number(row.nimBaseline);
                const isNeg = delta < 0;
                return (
                  <tr key={row.currency} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2 pr-4">
                      <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-semibold">
                        {row.currency}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-right font-mono text-xs tabular-nums">
                      {formatAmount(row.totalAssets)}
                    </td>
                    <td className="py-2 pr-4 text-right font-mono text-xs tabular-nums">
                      {formatAmount(row.niiBaseline)}
                    </td>
                    <td className="py-2 pr-4 text-right font-mono text-xs tabular-nums">
                      {formatAmount(row.niiStressed)}
                    </td>
                    <td className="py-2 pr-4 text-right font-mono text-xs tabular-nums">
                      {formatPct(row.nimBaseline)}
                    </td>
                    <td className="py-2 pr-4 text-right font-mono text-xs tabular-nums">
                      {formatPct(row.nimStressed)}
                    </td>
                    <td
                      className={cn(
                        'py-2 text-right font-mono text-xs font-semibold tabular-nums',
                        isNeg ? 'text-destructive' : 'text-green-600 dark:text-green-400',
                      )}
                    >
                      {isNeg ? '' : '+'}{(delta * 100).toFixed(2)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format a decimal-string ratio as a percentage with 2 dp (e.g. 0.025 → 2.50%). */
function formatPct(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return decimal;
  return (n * 100).toFixed(2) + '%';
}

/**
 * Format a large decimal-string amount as an abbreviated number.
 * e.g. 1_500_000_000 → 1.50B · 45_000_000 → 45.00M · −6_000 → −6.00K
 */
function formatAmount(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return decimal;
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + 'B';
  if (abs >= 1_000_000)     return (n / 1_000_000).toFixed(2) + 'M';
  if (abs >= 1_000)         return (n / 1_000).toFixed(2) + 'K';
  return n.toFixed(2);
}

// ── ApiErrorBanner ────────────────────────────────────────────────────────────

function ApiErrorBanner({ error }: { error: CubixApiError | Error }) {
  const isCubix = error instanceof CubixApiError;
  return (
    <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{error.message}</p>
        {isCubix ? (
          <p className="mt-0.5 text-xs opacity-80">
            code{' '}
            <code className="rounded bg-background/60 px-1 py-0.5">
              {(error as CubixApiError).code}
            </code>
          </p>
        ) : null}
      </div>
    </div>
  );
}
